import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Heart, MessageCircle, ImagePlus, MoreHorizontal, Trash2, X, Send, EyeOff, User as UserIcon, HandHeart, Search, Newspaper, Scale, Stethoscope, GraduationCap, UserPlus, HandHelping, Handshake, ArrowRight, Sparkles, Share2, Link as LinkIcon } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";

// Ícone + cor por tipo de aviso
const ANNOUNCEMENT_META = {
  postagem:    { Icon: Newspaper,    color: "#4A7C59", label: "Nova postagem" },
  direito:     { Icon: Scale,        color: "#2563eb", label: "Novo direito" },
  diagnostico: { Icon: Stethoscope,  color: "#0ea5e9", label: "Guia do Diagnóstico" },
  curso:       { Icon: GraduationCap,color: "#7c3aed", label: "Novo curso" },
  associado:   { Icon: UserPlus,     color: "#E89D7D", label: "Novo associado" },
  apoiador:    { Icon: HandHelping,  color: "#f59e0b", label: "Novo apoiador" },
  parceiro:    { Icon: Handshake,    color: "#0f766e", label: "Novo parceiro" },
};

const FEED_MD_COMPONENTS = {
  p: ({ children }) => <p className="mb-3 last:mb-0 whitespace-pre-wrap">{children}</p>,
  strong: ({ children }) => <strong className="font-bold text-[hsl(var(--foreground))]">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  ul: ({ children }) => <ul className="list-disc pl-5 space-y-1 mb-3">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-5 space-y-1 mb-3">{children}</ol>,
  a: ({ children, href }) => <a href={href} target="_blank" rel="noreferrer" className="text-[hsl(var(--primary))] underline underline-offset-2">{children}</a>,
  h1: ({ children }) => <h4 className="font-heading font-extrabold text-lg mb-2 mt-1">{children}</h4>,
  h2: ({ children }) => <h4 className="font-heading font-extrabold text-lg mb-2 mt-1">{children}</h4>,
  h3: ({ children }) => <h5 className="font-heading font-bold text-base mb-2 mt-1">{children}</h5>,
  blockquote: ({ children }) => <blockquote className="border-l-3 border-[hsl(var(--primary))]/40 pl-3 italic text-[hsl(var(--muted-foreground))] my-3">{children}</blockquote>,
};

// -------------------- SharePostButton --------------------
// Compartilhar post: WhatsApp (link direto), copiar link. Usa a Web Share API
// nativa quando disponível (mobile), com fallback pra menu manual.
function SharePostButton({ post }) {
  const [open, setOpen] = useState(false);
  const postUrl = typeof window !== "undefined"
    ? `${window.location.origin}/comunidade#post-${post.id}`
    : `/comunidade#post-${post.id}`;
  // Texto curto pra WhatsApp — evita colar 500 chars.
  const preview = (() => {
    const raw = (post.text || "").replace(/[*_`>#]/g, "").trim();
    const first = raw.split("\n")[0].slice(0, 120);
    return first ? `${first}${raw.length > 120 ? "…" : ""}\n\n` : "";
  })();
  const shareText = `${preview}Veja no Instituto Cuidar: ${postUrl}`;

  const tryNativeShare = async () => {
    if (typeof navigator === "undefined" || !navigator.share) return false;
    try {
      await navigator.share({ title: "Instituto Cuidar", text: preview.trim(), url: postUrl });
      return true;
    } catch { return false; }
  };

  const openWhatsApp = () => {
    const url = `https://api.whatsapp.com/send?text=${encodeURIComponent(shareText)}`;
    window.open(url, "_blank", "noopener");
    setOpen(false);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(postUrl);
      toast.success("Link copiado! É só colar onde quiser 🤗");
    } catch {
      toast.error("Não consegui copiar. Tente selecionar o link manualmente.");
    }
    setOpen(false);
  };

  const onClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Mobile / navegadores com Web Share API — usa o menu nativo do sistema.
    if (await tryNativeShare()) return;
    setOpen((v) => !v);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          data-testid={`feed-share-btn-${post.id}`}
          title="Compartilhar"
          className="inline-flex items-center gap-1.5 text-sm rounded-full px-3 py-1.5 text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]"
        >
          <Share2 className="h-4 w-4" />
          <span className="hidden sm:inline text-xs">Compartilhar</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52 rounded-2xl">
        <DropdownMenuItem onClick={openWhatsApp} data-testid={`feed-share-whatsapp-${post.id}`}>
          <MessageSquareIcon /> <span className="ml-2">WhatsApp</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={copyLink} data-testid={`feed-share-copy-${post.id}`}>
          <LinkIcon className="h-4 w-4" /> <span className="ml-2">Copiar link</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Ícone do WhatsApp — simples inline SVG (lucide não tem WhatsApp).
function MessageSquareIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4 text-[#25D366]">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.966-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.297-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.999-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
    </svg>
  );
}

function UserSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const t = q.trim();
    if (t.length < 2) { setResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/community/search-users", { params: { q: t } });
        setResults(data);
      } catch (_) { setResults([]); }
    }, 280);
    return () => clearTimeout(timer);
  }, [q]);
  return (
    <div className="surface p-4 mb-6">
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(var(--muted-foreground))]" />
        <Input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          placeholder="Buscar por família, cidade ou UF (ex: SP)…"
          className="pl-10 h-11 rounded-full bg-white"
          data-testid="community-search-input"
        />
      </div>
      {open && q.trim().length >= 2 && (
        <div className="mt-3 rounded-2xl border border-[hsl(var(--border))] bg-white overflow-hidden">
          {results.length === 0 && (
            <div className="p-4 text-sm text-[hsl(var(--muted-foreground))]">Nenhum resultado — tente outro nome ou UF.</div>
          )}
          {results.map((u) => (
            <div key={u.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-[hsl(var(--muted))]" data-testid={`community-search-result-${u.id}`}>
              <Avatar className="h-9 w-9">
                {u.profile_photo_path ? (
                  <img src={`${API}/files/${u.profile_photo_path}`} alt={u.name} className="w-full h-full object-cover" />
                ) : (
                  <AvatarFallback className="bg-[hsl(var(--primary))]/12 text-[hsl(var(--primary))] text-xs font-heading font-bold">{u.initials}</AvatarFallback>
                )}
              </Avatar>
              <div className="text-sm">
                <div className="font-heading font-bold">{u.name}</div>
                <div className="text-xs text-[hsl(var(--muted-foreground))]">{[u.city, u.state].filter(Boolean).join(" / ") || "—"}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function initials(name = "") {
  return name.split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase() || "").join("") || "?";
}

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "agora";
  if (diff < 3600) return `${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)} d`;
  return new Date(iso).toLocaleDateString("pt-BR");
}

export default function Community() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const authed = user && typeof user === "object";

  const load = () =>
    api.get("/feed").then(({ data }) => setPosts(data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  // Scroll até o post compartilhado quando abre com hash `#post-<id>` (link
  // de compartilhamento). Espera os cards renderizarem antes de rolar.
  useEffect(() => {
    if (loading || posts.length === 0) return;
    const hash = window.location.hash || "";
    if (!hash.startsWith("#post-")) return;
    const el = document.getElementById(hash.slice(1));
    if (el) {
      // Pequeno delay pra imagens do post terminarem de renderizar
      setTimeout(() => {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        el.classList.add("ring-2", "ring-[hsl(var(--primary))]", "ring-offset-4");
        setTimeout(() => {
          el.classList.remove("ring-2", "ring-[hsl(var(--primary))]", "ring-offset-4");
        }, 2400);
      }, 350);
    }
  }, [loading, posts]);

  const onCreated = (post) => setPosts((prev) => [post, ...prev]);
  const onDeleted = (id) => setPosts((prev) => prev.filter((p) => p.id !== id));
  const onUpdated = (id, patch) =>
    setPosts((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  const requireLogin = (from = "/comunidade") => navigate("/entrar", { state: { from } });

  return (
    <div>
      <section className="border-b border-[hsl(var(--border))] bg-[hsl(var(--muted))]/50">
        <div className="container-cuidar py-10">
          <div className="text-xs font-semibold tracking-widest uppercase text-[hsl(var(--accent))]">Comunidade</div>
          <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-black leading-tight">Nossa rede de cuidado</h1>
          <p className="mt-3 max-w-2xl text-[hsl(var(--muted-foreground))]">
            Um espaço seguro para desabafar, compartilhar o dia a dia, celebrar conquistas e trocar experiências com outras famílias. Você não está sozinho(a).
          </p>
        </div>
      </section>

      <section className="container-cuidar py-10 max-w-2xl">
        <UserSearch />
        {authed && <Composer user={user} onCreated={onCreated} />}
        <div className="mt-8 space-y-6">
          {loading && <div className="text-sm text-[hsl(var(--muted-foreground))]">Carregando comunidade…</div>}
          {!loading && posts.length === 0 && (
            <div className="surface p-10 text-center">
              <div className="font-heading text-lg font-bold">Nenhuma postagem por aqui ainda</div>
              <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">
                Assim que uma família publicar algo, aparece por aqui.
              </p>
            </div>
          )}
          {posts.map((p) => (
            <PostCard
              key={p.id}
              post={p}
              viewer={user}
              authed={authed}
              onDeleted={onDeleted}
              onUpdated={onUpdated}
              requireLogin={requireLogin}
            />
          ))}
        </div>
      </section>
    </div>
  );
}

function Composer({ user, onCreated }) {
  const [text, setText] = useState("");
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploadedPath, setUploadedPath] = useState(null);
  const [posting, setPosting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [anonymous, setAnonymous] = useState(false);
  const fileRef = useRef(null);

  const pickFile = () => fileRef.current?.click();

  const onFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Envie uma imagem."); return; }
    if (f.size > 6 * 1024 * 1024) { toast.error("Máximo 6MB."); return; }
    setImage(f);
    setPreview(URL.createObjectURL(f));
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", f);
      const { data } = await api.post("/feed/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadedPath(data.path);
    } catch (err) {
      toast.error("Falha no upload da imagem.");
      clearImage();
    } finally {
      setUploading(false);
    }
  };

  const clearImage = () => {
    setImage(null); setPreview(null); setUploadedPath(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async () => {
    if (!text.trim()) { toast.error("Escreva algo para publicar."); return; }
    setPosting(true);
    try {
      const { data } = await api.post("/feed", { text: text.trim(), image_path: uploadedPath || null, anonymous });
      onCreated(data);
      setText(""); clearImage(); setAnonymous(false);
    } catch (err) {
      toast.error("Não foi possível publicar.");
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="surface p-5 sm:p-6">
      <div className="flex gap-4">
        <Avatar className="h-11 w-11 mt-1">
          {user?.profile_photo_path && <AvatarImage src={`${API}/files/${user.profile_photo_path}`} alt={user?.name} />}
          <AvatarFallback className="bg-[hsl(var(--primary))]/12 text-[hsl(var(--primary))] font-heading font-bold">
            {initials(user?.name)}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1">
          <Textarea
            placeholder="Compartilhe sua rotina, dúvida, desabafo ou conquista…"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={3}
            className="rounded-2xl border-[hsl(var(--border))] bg-white resize-none focus-visible:ring-[hsl(var(--primary))]"
            data-testid="composer-text-input"
          />
          {preview && (
            <div className="relative mt-3 inline-block">
              <img src={preview} alt="prévia" className="max-h-64 rounded-xl border border-[hsl(var(--border))]" />
              <button
                onClick={clearImage}
                data-testid="composer-clear-image"
                className="absolute top-2 right-2 grid place-items-center h-8 w-8 rounded-full bg-black/60 text-white hover:bg-black/80"
                aria-label="Remover imagem"
              >
                <X className="h-4 w-4" />
              </button>
              {uploading && <div className="absolute inset-0 grid place-items-center bg-black/40 text-white rounded-xl text-sm">Enviando…</div>}
            </div>
          )}
          <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
            <div className="flex items-center gap-3">
              <input
                ref={fileRef} type="file" accept="image/*" hidden
                onChange={onFileChange}
                data-testid="composer-file-input"
              />
              <Button variant="outline" size="sm" onClick={pickFile} className="rounded-full" data-testid="composer-add-image-btn">
                <ImagePlus className="h-4 w-4 mr-1.5" /> Adicionar foto
              </Button>
              <label className="flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))] cursor-pointer select-none">
                <Checkbox
                  checked={anonymous}
                  onCheckedChange={(v) => setAnonymous(!!v)}
                  data-testid="composer-anonymous-checkbox"
                  className="rounded-md"
                />
                <span className="inline-flex items-center gap-1">
                  <EyeOff className="h-3.5 w-3.5" /> Desabafo anônimo
                </span>
              </label>
            </div>
            <Button
              onClick={submit}
              disabled={posting || uploading || !text.trim()}
              className="btn-pill bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90"
              data-testid="composer-submit-btn"
            >
              {posting ? "Publicando…" : (anonymous ? "Publicar anônimo" : "Publicar")}
            </Button>
          </div>
          {anonymous && (
            <div className="mt-2 text-xs text-[hsl(var(--muted-foreground))] bg-[hsl(var(--muted))]/70 rounded-xl px-3 py-2 flex items-start gap-2">
              <EyeOff className="h-3.5 w-3.5 mt-0.5 flex-none" />
              <span>Seu nome ficará oculto para outras famílias. Apenas você e a equipe do instituto verão sua identidade — para garantir a segurança da comunidade.</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PostCard({ post, viewer, authed, onDeleted, onUpdated, requireLogin }) {
  const canDelete = viewer && (viewer.role === "admin" || (post.author_id && viewer.id === post.author_id));
  const isAnon = post.is_anonymous;
  const isSystem = post.is_system || post.author_role === "system";
  const ann = post.announcement && ANNOUNCEMENT_META[post.announcement.kind] ? post.announcement : null;
  const [liking, setLiking] = useState(false);
  const [hugging, setHugging] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [posting, setPosting] = useState(false);

  const toggleLike = async () => {
    if (!authed) { requireLogin(); return; }
    if (liking) return;
    setLiking(true);
    try {
      const { data } = await api.post(`/feed/${post.id}/like`);
      onUpdated(post.id, { liked_by_me: data.liked, likes_count: data.likes_count });
    } catch (_) { /* ignore */ } finally { setLiking(false); }
  };

  const toggleHug = async () => {
    if (!authed) { requireLogin(); return; }
    if (hugging) return;
    setHugging(true);
    try {
      const { data } = await api.post(`/feed/${post.id}/hug`);
      onUpdated(post.id, { hugged_by_me: data.hugged, hugs_count: data.hugs_count });
    } catch (_) { /* ignore */ } finally { setHugging(false); }
  };

  const remove = async () => {
    if (!window.confirm("Excluir esta postagem?")) return;
    try {
      await api.delete(`/feed/${post.id}`);
      onDeleted(post.id);
      toast.success("Postagem excluída.");
    } catch (_) { toast.error("Não foi possível excluir."); }
  };

  const sendComment = async () => {
    if (!authed) { requireLogin(); return; }
    if (!commentText.trim()) return;
    setPosting(true);
    try {
      const { data } = await api.post(`/feed/${post.id}/comments`, { text: commentText.trim() });
      onUpdated(post.id, {
        comments: [...post.comments, data],
        comments_count: post.comments_count + 1,
      });
      setCommentText("");
    } catch (_) { toast.error("Não foi possível comentar."); } finally { setPosting(false); }
  };

  const removeComment = async (commentId) => {
    try {
      await api.delete(`/feed/${post.id}/comments/${commentId}`);
      onUpdated(post.id, {
        comments: post.comments.filter((c) => c.id !== commentId),
        comments_count: Math.max(0, post.comments_count - 1),
      });
    } catch (_) { toast.error("Não foi possível excluir."); }
  };

  const AnnIcon = ann ? ANNOUNCEMENT_META[ann.kind].Icon : null;
  const annColor = ann ? ANNOUNCEMENT_META[ann.kind].color : null;
  // Cor primária do Instituto — usada quando o post é oficial (isSystem sem ann).
  const INSTITUTO_COLOR = "#4A7C59";

  return (
    <article
      id={`post-${post.id}`}
      className={`surface p-5 sm:p-6 ${ann ? "border-l-4 relative" : ""} ${isSystem && !ann ? "border-l-4 relative" : ""}`}
      style={
        ann
          ? { borderLeftColor: annColor, background: `linear-gradient(180deg, ${annColor}0F 0%, transparent 40%)` }
          : (isSystem
            ? { borderLeftColor: INSTITUTO_COLOR, background: `linear-gradient(180deg, ${INSTITUTO_COLOR}0D 0%, transparent 45%)` }
            : undefined)
      }
      data-testid={`feed-post-${post.id}`}
    >
      {ann && (
        <div className="absolute top-4 right-4 inline-flex items-center gap-1 rounded-full px-2 sm:px-2.5 py-1 text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-white" style={{ background: annColor }} data-testid={`feed-announcement-badge-${ann.kind}`}>
          <Sparkles className="h-3 w-3" /> Aviso
        </div>
      )}
      {isSystem && !ann && (
        <div className="absolute top-4 right-4 inline-flex items-center gap-1 rounded-full px-2 sm:px-2.5 py-1 text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-white" style={{ background: INSTITUTO_COLOR }} data-testid={`feed-instituto-badge-${post.id}`}>
          <Sparkles className="h-3 w-3" /> Oficial
        </div>
      )}
      <header className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          {ann ? (
            <div
              className="h-11 w-11 rounded-full grid place-items-center text-white shrink-0"
              style={{ background: annColor }}
              aria-hidden="true"
            >
              <AnnIcon className="h-5 w-5" />
            </div>
          ) : (
            <Avatar className="h-11 w-11">
              {isSystem ? (
                <AvatarImage src="/logo-avatar.png" alt="Instituto Cuidar" className="object-cover" />
              ) : !isAnon && post.author_photo_path && (
                <AvatarImage src={`${API}/files/${post.author_photo_path}`} alt={post.author_name} />
              )}
              <AvatarFallback className={`font-heading font-bold ${
                isAnon
                  ? "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]"
                  : isSystem
                    ? "bg-white text-[hsl(var(--primary))] border border-[hsl(var(--primary))]/25"
                    : post.author_role === "admin"
                      ? "bg-[hsl(var(--secondary))]/25 text-[hsl(var(--secondary))]"
                      : "bg-[hsl(var(--primary))]/12 text-[hsl(var(--primary))]"
              }`}>
                {isAnon ? <UserIcon className="h-5 w-5" /> : (isSystem ? "IC" : post.author_initials)}
              </AvatarFallback>
            </Avatar>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap pr-20 sm:pr-24">
              <span className="font-heading font-bold truncate max-w-full">{post.author_name}</span>
              {ann && (
                <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full text-white font-semibold" style={{ background: annColor }}>
                  {ANNOUNCEMENT_META[ann.kind].label}
                </span>
              )}
              {!ann && isAnon && (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] px-2 py-0.5 rounded-full">
                  <EyeOff className="h-3 w-3" /> Anônimo
                </span>
              )}
              {!ann && !isAnon && post.author_role === "admin" && (
                <span className="text-[10px] uppercase tracking-widest bg-[hsl(var(--secondary))]/20 text-[hsl(var(--secondary))] px-2 py-0.5 rounded-full">Equipe</span>
              )}
              {/* Badge "Instituto" só aparece em telas médias+ — no mobile o
                  "OFICIAL" no canto já basta e evita sobreposição. */}
              {isSystem && !ann && (
                <span className="hidden sm:inline text-[10px] uppercase tracking-widest bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] px-2 py-0.5 rounded-full">Instituto</span>
              )}
            </div>
            <div className="text-xs text-[hsl(var(--muted-foreground))]">{timeAgo(post.created_at)}</div>
          </div>
        </div>
        {canDelete && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="p-2 rounded-full hover:bg-[hsl(var(--muted))]" data-testid={`feed-post-menu-${post.id}`} aria-label="Opções">
                <MoreHorizontal className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-2xl">
              <DropdownMenuItem onClick={remove} className="text-[hsl(var(--destructive))]" data-testid={`feed-post-delete-${post.id}`}>
                <Trash2 className="h-4 w-4 mr-2" /> Excluir
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </header>

      {ann ? (
        <div className="mt-4">
          <h3 className="font-heading text-xl sm:text-2xl font-extrabold leading-tight" data-testid={`feed-announcement-title-${post.id}`}>{ann.title}</h3>
          {ann.subtitle && (
            <p className="mt-2 text-[hsl(var(--muted-foreground))] leading-relaxed">{ann.subtitle}</p>
          )}
          {ann.link && (
            <Link
              to={ann.link}
              className="mt-4 inline-flex items-center gap-1.5 rounded-full h-10 px-4 text-sm font-semibold text-white transition-transform hover:scale-[1.02]"
              style={{ background: annColor }}
              data-testid={`feed-announcement-link-${post.id}`}
            >
              Ver detalhes <ArrowRight className="h-4 w-4" />
            </Link>
          )}
        </div>
      ) : (
        <div className="mt-4 text-[15px] leading-relaxed feed-markdown">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={FEED_MD_COMPONENTS}
          >
            {post.text}
          </ReactMarkdown>
        </div>
      )}

      {(post.image_url || post.image_path) && (
        <div className="mt-4 rounded-2xl overflow-hidden border border-[hsl(var(--border))] bg-[hsl(var(--muted))]">
          <img
            src={post.image_url || `${API}/files/${post.image_path}`}
            alt="foto da postagem"
            className="w-full max-h-[520px] object-cover"
            loading="lazy"
          />
        </div>
      )}

      <div className="mt-4 flex items-center gap-2 border-t border-[hsl(var(--border))] pt-4">
        <button
          onClick={toggleLike}
          data-testid={`feed-like-btn-${post.id}`}
          className={`inline-flex items-center gap-1.5 text-sm rounded-full px-3 py-1.5 transition-colors ${post.liked_by_me ? "text-[hsl(var(--destructive))] bg-[hsl(var(--destructive))]/10" : "text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]"}`}
        >
          <Heart className={`h-4 w-4 ${post.liked_by_me ? "fill-current" : ""}`} />
          <span>{post.likes_count}</span>
        </button>
        <button
          onClick={toggleHug}
          data-testid={`feed-hug-btn-${post.id}`}
          title="Enviar um abraço"
          className={`inline-flex items-center gap-1.5 text-sm rounded-full px-3 py-1.5 transition-colors ${post.hugged_by_me ? "text-[hsl(var(--secondary))] bg-[hsl(var(--secondary))]/12" : "text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]"}`}
        >
          <HandHeart className={`h-4 w-4 ${post.hugged_by_me ? "fill-current" : ""}`} />
          <span>{post.hugs_count || 0}</span>
          <span className="hidden sm:inline text-xs">abraço{(post.hugs_count || 0) === 1 ? "" : "s"}</span>
        </button>
        <button
          onClick={() => setCommentsOpen((v) => !v)}
          data-testid={`feed-comments-toggle-${post.id}`}
          className="inline-flex items-center gap-1.5 text-sm rounded-full px-3 py-1.5 text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))] ml-auto"
        >
          <MessageCircle className="h-4 w-4" />
          <span>{post.comments_count}</span>
        </button>
        <SharePostButton post={post} />
      </div>

      {commentsOpen && (
        <div className="mt-4 space-y-3">
          {post.comments.map((c) => (
            <div key={c.id} className="flex items-start gap-3" data-testid={`feed-comment-${c.id}`}>
              <Avatar className="h-8 w-8">
                {c.author_photo_path && (
                  <AvatarImage src={`${API}/files/${c.author_photo_path}`} alt={c.author_name} />
                )}
                <AvatarFallback className="bg-[hsl(var(--muted))] text-xs font-heading font-bold">{c.author_initials}</AvatarFallback>
              </Avatar>
              <div className="flex-1 rounded-2xl bg-[hsl(var(--muted))] px-4 py-2.5">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-heading font-bold">{c.author_name}</div>
                  <div className="text-[11px] text-[hsl(var(--muted-foreground))]">{timeAgo(c.created_at)}</div>
                </div>
                <div className="text-sm mt-0.5 whitespace-pre-wrap">{c.text}</div>
              </div>
              {viewer && (viewer.role === "admin" || viewer.id === c.author_id) && (
                <button className="p-2 rounded-full hover:bg-[hsl(var(--muted))]" onClick={() => removeComment(c.id)} data-testid={`feed-comment-delete-${c.id}`} aria-label="Excluir">
                  <Trash2 className="h-4 w-4 text-[hsl(var(--destructive))]" />
                </button>
              )}
            </div>
          ))}
          <div className="flex items-start gap-3 pt-2">
            <Avatar className="h-8 w-8">
              {viewer?.profile_photo_path && (
                <AvatarImage src={`${API}/files/${viewer.profile_photo_path}`} alt={viewer?.name} />
              )}
              <AvatarFallback className="bg-[hsl(var(--primary))]/12 text-[hsl(var(--primary))] text-xs font-heading font-bold">{initials(viewer?.name)}</AvatarFallback>
            </Avatar>
            <div className="flex-1 flex gap-2">
              <Input
                placeholder="Escreva um comentário…"
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendComment()}
                className="rounded-full h-10 bg-white"
                data-testid={`feed-comment-input-${post.id}`}
              />
              <Button
                size="icon"
                onClick={sendComment} disabled={posting || !commentText.trim()}
                className="rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90"
                data-testid={`feed-comment-send-${post.id}`}
                aria-label="Enviar comentário"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </article>
  );
}
